import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import IosShareIcon from '@mui/icons-material/IosShare'

import { Button, type ButtonStyles } from '@nexiahome/ui-common/components/primitives/buttons'
import LegacyButton from 'components/LegacyButton'

import Modal from 'components/Modal'

import RemoteConfiguration from 'pages/Customer/RemoteConfiguration'
import AssignDevice from 'pages/Customer/AssignDevice'
import CustomerInfo from 'pages/Customer/CustomerInfo'
import CustomerForm from 'pages/shared/CustomerForm'
import Note from 'pages/shared/Note'
import Pin from 'pages/shared/Pin'
import SystemReportsModal from 'pages/Customer/SystemReportsModal'

import { useSessionContext } from 'hooks/SessionContext'

import { streetAddress, cityStateZip, phoneNumber, customerDisplayName } from 'helpers/formatters'
import GA4 from 'helpers/ga4'

import Api from 'services/api'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

import { Customer } from 'types/Customer'
import { DeviceBase } from 'types/Device'
import { DeviceTypes } from 'helpers/constants'

type Props = {
  customer: Customer,
  activeDevice: DeviceBase | undefined,
  className?: string
  devices: Array<DeviceBase>
  onSaveCallback: (deviceId: string) => void
  refreshPageCallback: () => void
}
const disableReportForDeviceStatus: Record<string, boolean> = {
  'NOT ENROLLED': true,
  'OPTED OUT': true
}

const CustomerDetails = ({ customer: customerProp, activeDevice, className = '', devices = [], onSaveCallback, refreshPageCallback }: Props) => {
  const { session: { readOnly } } = useSessionContext()

  const [customer, setCustomer] = useState(customerProp)
  const [note, setNote] = useState(customerProp.note)

  useEffect(() => {
    setCustomer(customerProp)
    setNote(customerProp.note)
  }, [customerProp])

  const [displayCustomerForm, setDisplayCustomerForm] = useState(false)
  const [showAssignDeviceModel, setShowAssignDeviceModel] = useState(false)
  const [showReportDownloadSettingsModal, setShowReportDownloadSettingsModal] = useState(false)

  const displayName = customerDisplayName(customer.firstName, customer.lastName, customer.companyName)

  const onSaveNote = async (updatedNote: string) => {
    if (updatedNote === note) {
      return true
    }

    const editCustomer: Customer = { ...customer, note: updatedNote }

    GA4.sendEvent({ category: 'Customer Detail Page', action: 'Click Save Customer Note' })

    return await Api.Customer.update(customer.dealerId, customer.id, editCustomer)
      .then(() => {
        setNote(updatedNote)
        return true
      })
      .catch(() => {
        return false
      })
  }

  const allowedDevices = devices?.filter(({ status, deviceType }) => !disableReportForDeviceStatus[status] && deviceType.toLowerCase() !== DeviceTypes.HEHM) ?? []
  const isReportDownloadEnabled = allowedDevices.length > 0
  const hasHehmDevices = devices.some(({ deviceType }) => deviceType.toLowerCase() === DeviceTypes.HEHM)

  return (
    <>
      <S.Container {...{ className }}>
        <S.Row>
          <S.NameContainer>
            <S.Pin isPinned={customer.pinned} pinnedAt={customer.pinnedAt} displayUnpinned customerName={displayName} customerId={customer.id} />
            <S.Name>{displayName}</S.Name>
            <CustomerInfo
              companyName={customer.companyName}
              name={displayName}
              email={customer.email}
              phone={phoneNumber(customer.phone)}
              address={streetAddress(customer.address1, customer.address2)}
              city={cityStateZip(customer.city, customer.state, customer.zip)}
            />
            <S.Note note={note} onSaveNoteCallback={onSaveNote} variant="large" />
            {isReportDownloadEnabled && (
              <S.ReportButton onClick={() => setShowReportDownloadSettingsModal(true)} type="button" variant="link"><IosShareIcon />System Report</S.ReportButton>
            )}
          </S.NameContainer>
        </S.Row>
        <S.Row>
          <S.ButtonContainer>
            {activeDevice && <S.RemoteConfiguration device={activeDevice} />}
            <S.EditButton decoration="secondary" icon="edit" text="EDIT CUSTOMER" styles={EditButtonStyle} onPress={() => setDisplayCustomerForm(true)} />
            <S.AssignButton decoration="positive" icon="new" text="ASSIGN SYSTEM" styles={AssignButtonStyle} onPress={() => setShowAssignDeviceModel(true)} />
          </S.ButtonContainer>
        </S.Row>
      </S.Container>

      {showAssignDeviceModel && <AssignDevice dealerId={customer.dealerId} customerId={customer.id} onSaveCallback={(deviceId) => onSaveCallback(deviceId)} closeCallback={() => setShowAssignDeviceModel(false)} />}
      {showReportDownloadSettingsModal && devices.length > 0 && (
        <SystemReportsModal
          isDisplayed={showReportDownloadSettingsModal}
          closeCallback={() => setShowReportDownloadSettingsModal(false)}
          devices={allowedDevices}
          customer={customer}
          onSaveCallback={(deviceId) => onSaveCallback(deviceId)}
          hasHehmDevices={hasHehmDevices}
        />
      )}
      <Modal isDisplayed={displayCustomerForm} closeCallback={() => setDisplayCustomerForm(false)} variant="sm" subTitle="Customer Info" title="Edit Customer" >
        <CustomerForm
          customer={customer}
          dealerId={customer.dealerId}
          readOnly={readOnly}
          title="Edit Customer"
          readOnlyMessage="Read-only view: can't edit customer"
          closeCallback={() => setDisplayCustomerForm(false)}
          onSaveCustomerCallback={() => refreshPageCallback()}
          displayDeleteButton
        />
      </Modal>
    </>
  )
}

export default CustomerDetails

const EditButtonStyle: ButtonStyles = { button: { marginRight: 15, borderRadius: 14 } }
const AssignButtonStyle: ButtonStyles = { button: { borderRadius: 14 } }

const S = {
  Container: styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    background-color: ${color('paneBackground')};

    @media ${screenSize.small} {
      flex-direction: column;
    }
  `,
  Row: styled.div`
    display: flex;

    @media ${screenSize.medium} {
      width: 100%;
    }

    @media ${screenSize.small} {
      flex-direction: column;
    }
  `,
  NameContainer: styled.div`
    display: flex;
    align-items: center;

    @media ${screenSize.small} {
      flex-wrap: wrap;
    }
  `,
  Name: styled.h1`
    padding-left: 15px;
    text-transform: capitalize;
    font-size: 2rem;
    font-weight: 600;
    color: ${color('customerNameText')};

    @media ${screenSize.small} {
      padding-left: 0;
      font-size: 1.225rem;
      margin: 0px;
    }
  `,
  Note: styled(Note)`
    height: 26px;
  `,
  Pin: styled(Pin)`
    font-size: 18px;
    padding: 5px;
  `,
  ButtonContainer: styled.div`
    display: flex;
    align-items: start;
    justify-content: flex-end;
    gap: 10px;

    & > button {
      margin: 0 !important;
      height: 34px !important;
      min-width: 64px !important;
    }

    @media ${screenSize.medium} {
      width: 100%;
      margin-top: 10px;
    }

    @media ${screenSize.small} {
      display: flex;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: auto auto;
      gap: 10px;
      align-items: start;
    }
  `,
  RemoteConfiguration: styled(RemoteConfiguration)`
    @media ${screenSize.small} {
      grid-row: 1 / span 2;
      grid-column: 1;
    }
  `,
  EditButton: styled(Button)`   
    @media ${screenSize.small} {
      grid-row: 1;
      grid-column: 2;
    }
  `,
  AssignButton: styled(Button)`
    @media ${screenSize.small} {
      grid-row: 2;
      grid-column: 2;
    }
  `,
  ReportButton: styled(LegacyButton)`
    text-transform: uppercase;
    margin-left: 15px;
    position: relative;
    & svg {
      margin-right: 4px;
      position: relative;
      top: 3px;
    }
  `
}
